import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Check, Crown, Loader2 } from "lucide-react";
import {
  fetchActiveSubscriptionForUser,
  type SubscriptionPlan,
  type SubscriptionRow,
} from "@/lib/subscriptions";
import { trackEvent } from "@/lib/posthog";

type Plan = SubscriptionPlan;

interface CheckoutResponse {
  url?: string;
  error?: string;
}

interface PlanDef {
  price: number;
  title: string;
  subtitle: string;
  bullets: ReadonlyArray<string>;
  highlight?: boolean;
}

const PLAN_DETAILS: Readonly<Record<Plan, PlanDef>> = {
  founders: {
    price: 79,
    title: "Builder — Founders",
    subtitle: "Pierwsze 100 ekip. Cena zamrożona na zawsze.",
    bullets: [
      "Nielimitowane oferty",
      "79 zł/mies — gwarantowane na zawsze (price-lock)",
      "Wszystkie przyszłe funkcje PRO w cenie",
      "Anuluj kiedy chcesz",
    ],
    highlight: true,
  },
  normal: {
    price: 149,
    title: "Builder",
    subtitle: "Plan standardowy dla wszystkich kolejnych ekip.",
    bullets: [
      "Nielimitowane oferty",
      "149 zł/mies",
      "Wszystkie funkcje PRO",
      "Anuluj kiedy chcesz",
    ],
  },
} as const;

function getInvokeStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const ctx = (error as { context?: unknown }).context;
  if (!ctx || typeof ctx !== "object") return null;
  const status = (ctx as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

export default function PricingPage(): JSX.Element {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [activeSub, setActiveSub] = useState<SubscriptionRow | null>(null);
  const [subLoading, setSubLoading] = useState<boolean>(true);
  const [foundersSoldOut, setFoundersSoldOut] = useState<boolean>(false);
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setActiveSub(null);
      setSubLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const sub = await fetchActiveSubscriptionForUser(user.id);
      if (cancelled) return;
      setActiveSub(sub);
      setSubLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  const handleSoldOut = (): void => {
    setFoundersSoldOut(true);
    toast({
      title: "Miejsca założycielskie wyprzedane",
      description: "Wróć po 149 zł/mies w planie Builder.",
      variant: "destructive",
    });
  };

  const startCheckout = async (plan: Plan): Promise<void> => {
    if (!user) {
      navigate("/login", { state: { returnTo: "/pricing" } });
      return;
    }
    setPendingPlan(plan);
    try {
      const { data, error } = await supabase.functions.invoke<CheckoutResponse>(
        "create-checkout-session",
        { body: { plan } },
      );
      if (error) {
        if (getInvokeStatus(error) === 409) {
          handleSoldOut();
          return;
        }
        toast({
          title: "Nie udało się rozpocząć płatności",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      if (data?.error === "sold_out") {
        handleSoldOut();
        return;
      }
      if (!data?.url) {
        toast({
          title: "Brak adresu Stripe",
          description: "Spróbuj ponownie za chwilę.",
          variant: "destructive",
        });
        return;
      }
      trackEvent("checkout_started");
      window.location.href = data.url;
    } finally {
      setPendingPlan(null);
    }
  };

  const renderTile = (plan: Plan): JSX.Element => {
    const def = PLAN_DETAILS[plan];
    const isUserActiveOnThisPlan = !subLoading && activeSub?.plan === plan;
    const isUserActiveOnOtherPlan =
      !subLoading && activeSub !== null && activeSub.plan !== plan;
    const isPlanSoldOut = plan === "founders" && foundersSoldOut;
    const isLoadingThis = pendingPlan === plan;
    const disabled =
      isUserActiveOnThisPlan ||
      isUserActiveOnOtherPlan ||
      isPlanSoldOut ||
      pendingPlan !== null;

    let ctaLabel = "Zacznij teraz";
    if (isUserActiveOnThisPlan) ctaLabel = "Aktywny plan";
    else if (isUserActiveOnOtherPlan) ctaLabel = "Masz aktywny inny plan";
    else if (isPlanSoldOut) ctaLabel = "Wyprzedane";

    return (
      <div
        className="flex flex-col rounded-2xl border-2 bg-card p-6 shadow-sm"
        style={{
          borderColor: def.highlight
            ? "var(--color-accent-primary)"
            : "var(--color-border-default)",
        }}
      >
        <div className="mb-3 flex items-center gap-2">
          {def.highlight && <Crown className="h-5 w-5 text-primary" />}
          <h2 className="text-xl font-extrabold text-foreground">{def.title}</h2>
        </div>
        <p className="mb-6 text-sm text-muted-foreground">{def.subtitle}</p>
        <p className="mb-6">
          <span className="font-mono text-4xl font-extrabold tabular-nums text-foreground">
            {def.price}
          </span>
          <span className="ml-1 text-sm text-muted-foreground">zł / mies</span>
        </p>
        <ul className="mb-6 space-y-2 text-sm">
          {def.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          disabled={disabled}
          onClick={() => {
            void startCheckout(plan);
          }}
          className="mt-auto"
        >
          {isLoadingThis ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Przekierowuję…
            </span>
          ) : (
            ctaLabel
          )}
        </Button>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-foreground">Wybierz plan</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Anuluj kiedy chcesz. Płatność miesięczna — karta, BLIK lub Przelewy24.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {renderTile("founders")}
          {renderTile("normal")}
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Płatności obsługuje Stripe. Faktura VAT na e-mail po zaksięgowaniu.
        </p>
      </div>
    </AppLayout>
  );
}
